import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Copy, RefreshCw, Gift, CheckCircle2, AlertCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const INPUT = { width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '12px 14px', fontSize: '14px', outline: 'none', borderRadius: 0, fontFamily: 'inherit' };
const LABEL = { display: 'block', fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--pp-text-3)', marginBottom: '6px' };

const STATUS_META = {
  waiting: { color: '#FFB000', label: 'Waiting for Payment', icon: RefreshCw },
  confirming: { color: '#2979FF', label: 'Confirming on-chain', icon: RefreshCw },
  confirmed: { color: '#00FF94', label: 'Confirmed', icon: CheckCircle2 },
  sending: { color: '#2979FF', label: 'Sending', icon: RefreshCw },
  finished: { color: '#00FF94', label: 'Payment Complete', icon: CheckCircle2 },
  failed: { color: '#FF4949', label: 'Failed', icon: AlertCircle },
  expired: { color: '#FF4949', label: 'Expired', icon: AlertCircle },
  refunded: { color: '#A0A0A0', label: 'Refunded', icon: AlertCircle },
  api_error: { color: '#FF4949', label: 'Gateway Error', icon: AlertCircle },
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const checkoutType = params.get('type') === 'funded' ? 'funded' : 'subscription';
  const chargeId = params.get('charge') || '';
  const [customCharge, setCustomCharge] = useState(null);
  const [plans, setPlans] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(params.get('plan') || (params.get('type') === 'funded' ? 'funded_200k' : 'monthly_single'));
  const [selectedCurrency, setSelectedCurrency] = useState('btc');
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [asPasscode, setAsPasscode] = useState('');
  const [asNotes, setAsNotes] = useState('');
  const [asSubmitting, setAsSubmitting] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showDiscountRequest, setShowDiscountRequest] = useState(false);
  const [discountReason, setDiscountReason] = useState('');
  const [discountRequestedPct, setDiscountRequestedPct] = useState(10);
  const [submittingDiscountRequest, setSubmittingDiscountRequest] = useState(false);
  const [myFundedApplication, setMyFundedApplication] = useState(null);
  const [meKycVerified, setMeKycVerified] = useState(true); // assume true to avoid flash
  const pollRef = useRef(null);

  useEffect(() => {
    api.getMe().then(u => setMeKycVerified(!!u.is_verified)).catch(() => {});
  }, []);

  useEffect(() => {
    // Custom charge mode — fetch charge and skip plan listing
    if (chargeId) {
      api.getCharge(chargeId)
        .then(c => setCustomCharge(c))
        .catch(e => toast.error(e.message));
      api.getPaymentCurrencies().then(setCurrencies).catch(() => {});
      return;
    }
    Promise.all([api.getPaymentPlans(), api.getPaymentCurrencies()])
      .then(([p, c]) => {
        const filtered = p.filter(x => (x.category || 'subscription') === checkoutType);
        setPlans(filtered);
        setCurrencies(c);
      })
      .catch(e => toast.error(e.message));
    if (checkoutType === 'subscription') {
      api.getMe().then(u => {
        if (u.is_active_student || u.is_paid) {
          toast.info('You already have an active subscription');
          setTimeout(() => navigate('/dashboard'), 1200);
        }
      }).catch(() => {});
    }
    if (checkoutType === 'funded') {
      // Find user's most recent applied funded application (to attach to AS request)
      api.getMyFundedAccounts?.().then(list => {
        const pending = (list || []).find(x => x.status === 'applied' || x.status === 'pending');
        if (pending) setMyFundedApplication(pending);
      }).catch(() => {});
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [navigate, checkoutType, chargeId]);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    try {
      const res = await api.validateDiscount(discountCode.trim().toUpperCase());
      setDiscountApplied(res);
      toast.success(`${res.percent}% discount applied!`);
    } catch (e) {
      setDiscountApplied(null);
      toast.error(e.message);
    }
  };

  const redeemASPasscode = async () => {
    if (!asPasscode.trim()) return;
    try {
      await api.activateStudentPasscode(asPasscode.trim());
      toast.success('Prepayment verified! Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (e) {
      const msg = e.message || 'Invalid AS passcode';
      if (msg.toLowerCase().includes('identity') || msg.toLowerCase().includes('kyc') || msg.toLowerCase().includes('verif')) {
        toast.error('Verify your identity first to activate this passcode');
        setTimeout(() => navigate('/verification'), 1500);
      } else {
        toast.error(msg);
      }
    }
  };

  const submitFundedASRequest = async () => {
    if (!asPasscode.trim()) return;
    if (!myFundedApplication) {
      toast.error('No funded application found. Please apply first via Link Broker → Apply for ProfitPulse Funded.');
      return;
    }
    setAsSubmitting(true);
    try {
      await api.fundedAsRequest({
        funded_account_id: myFundedApplication.funded_account_id,
        account_size: myFundedApplication.size,
        as_passcode: asPasscode.trim(),
        notes: asNotes || '',
      });
      toast.success('Request submitted! Admin will review and email you shortly.');
      setTimeout(() => navigate('/funded-accounts'), 1500);
    } catch (e) {
      const msg = e.message || 'Could not submit request';
      if (msg.toLowerCase().includes('identity') || msg.toLowerCase().includes('kyc') || msg.toLowerCase().includes('verif')) {
        toast.error('Verify your identity first to submit this request');
        setTimeout(() => navigate('/verification'), 1500);
      } else {
        toast.error(msg);
      }
    }
    setAsSubmitting(false);
  };

  const createInvoiceFromCharge = async () => {
    if (!customCharge) return;
    setCreating(true);
    try {
      const res = await api.createInvoiceForCharge(customCharge.id, selectedCurrency);
      setInvoice(res);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const upd = await api.getInvoiceStatus(res.id);
          setInvoice(upd);
          if (['finished', 'confirmed', 'failed', 'expired', 'refunded'].includes(upd.status)) {
            clearInterval(pollRef.current);
            if (upd.status === 'finished' || upd.status === 'confirmed') {
              toast.success('Payment confirmed! Funded account will be provisioned shortly.');
              setTimeout(() => navigate('/funded-accounts'), 2000);
            }
          }
        } catch { /* ignore */ }
      }, 6000);
    } catch (e) {
      toast.error(e.message);
    }
    setCreating(false);
  };

  const createInvoice = async () => {
    setCreating(true);
    try {
      const res = await api.createInvoice({
        plan_id: selectedPlan,
        pay_currency: selectedCurrency,
        discount_code: discountApplied ? discountApplied.code : null,
      });
      setInvoice(res);
      // Start polling
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const upd = await api.getInvoiceStatus(res.id);
          setInvoice(upd);
          if (['finished', 'confirmed', 'failed', 'expired', 'refunded'].includes(upd.status)) {
            clearInterval(pollRef.current);
            if (upd.status === 'finished' || upd.status === 'confirmed') {
              toast.success('Payment confirmed! Activating your account...');
              setTimeout(() => navigate('/dashboard'), 2000);
            }
          }
        } catch { /* ignore */ }
      }, 6000);
    } catch (e) {
      toast.error(e.message);
    }
    setCreating(false);
  };

  const submitDiscountRequest = async () => {
    setSubmittingDiscountRequest(true);
    try {
      await api.requestDiscount({
        reason: discountReason,
        requested_percent: Number(discountRequestedPct),
        plan_id: selectedPlan,
      });
      toast.success('Discount request submitted. Admin will review and email you the code.');
      setShowDiscountRequest(false);
      setDiscountReason('');
    } catch (e) {
      toast.error(e.message);
    }
    setSubmittingDiscountRequest(false);
  };

  const selectedPlanObj = plans.find(p => p.id === selectedPlan);
  const basePrice = customCharge ? customCharge.amount_usd : (selectedPlanObj?.price_usd || 0);
  const discountedPrice = !customCharge && discountApplied ? +(basePrice * (1 - discountApplied.percent / 100)).toFixed(2) : basePrice;

  // Invoice view
  if (invoice) {
    const meta = STATUS_META[invoice.status] || STATUS_META.waiting;
    const Icon = meta.icon;
    const isDone = ['finished', 'confirmed'].includes(invoice.status);
    return (
      <div className="page-container" data-testid="invoice-view">
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)' }}>
          <button onClick={() => { if (pollRef.current) clearInterval(pollRef.current); setInvoice(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>PAY WITH CRYPTO</h1>
        </div>

        <div style={{ padding: 20, maxWidth: 520, margin: '0 auto' }}>
          <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.15em', color: 'var(--pp-text-3)', fontWeight: 600 }}>PLAN</p>
                <p style={{ margin: '4px 0 0', fontSize: 15, fontWeight: 700 }}>{invoice.plan_label}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.15em', color: 'var(--pp-text-3)', fontWeight: 600 }}>AMOUNT</p>
                <p style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 700, fontFamily: 'monospace' }}>${invoice.amount_usd}</p>
                {invoice.discount_percent > 0 && (
                  <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-success)' }}>-{invoice.discount_percent}% off</p>
                )}
              </div>
            </div>

            {!isDone && invoice.pay_address && (
              <>
                <div style={{ background: '#fff', padding: 20, textAlign: 'center', marginBottom: 16 }}>
                  <QRCodeSVG value={invoice.pay_address} size={220} level="M" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <p style={LABEL}>SEND EXACTLY</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--pp-overlay)', padding: 14, border: '1px solid var(--pp-border)' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: 'var(--pp-success)' }}>{invoice.pay_amount}</span>
                    <span style={{ fontSize: 12, color: 'var(--pp-text-3)', fontWeight: 700, letterSpacing: '0.1em' }}>{(invoice.pay_currency || '').toUpperCase()}</span>
                    <button data-testid="copy-amount-btn" onClick={() => { navigator.clipboard.writeText(String(invoice.pay_amount)); toast.success('Amount copied'); }} style={{ marginLeft: 'auto', background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '4px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}><Copy size={11} />COPY</button>
                  </div>
                </div>
                <div>
                  <p style={LABEL}>TO ADDRESS</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--pp-overlay)', padding: 14, border: '1px solid var(--pp-border)' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--pp-text-2)', wordBreak: 'break-all', flex: 1 }}>{invoice.pay_address}</span>
                    <button data-testid="copy-address-btn" onClick={() => { navigator.clipboard.writeText(invoice.pay_address); toast.success('Address copied'); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '4px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}><Copy size={11} />COPY</button>
                  </div>
                </div>
              </>
            )}

            <div style={{ marginTop: 18, padding: 14, background: meta.color + '15', border: `1px solid ${meta.color}40`, display: 'flex', alignItems: 'center', gap: 10 }} data-testid="invoice-status">
              <Icon size={16} color={meta.color} style={!isDone ? { animation: 'spin 2s linear infinite' } : {}} />
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: meta.color }}>{meta.label}</p>
            </div>
            {invoice.error && <p style={{ marginTop: 10, fontSize: 11, color: 'var(--pp-text-3)' }}>{invoice.error}</p>}
          </div>

          <p style={{ textAlign: 'center', color: 'var(--pp-text-3)', fontSize: 11, margin: 0 }}>
            Payments are processed via NOWPayments. Your account will activate automatically once the network confirms.
          </p>
        </div>
      </div>
    );
  }

  // Plan/Currency selection view
  return (
    <div className="page-container" data-testid="checkout-page">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{checkoutType === 'funded' ? 'PAY FOR FUNDED ACCOUNT' : 'SUBSCRIBE'}</h1>
      </div>

      <div style={{ padding: 20, maxWidth: 640, margin: '0 auto' }}>
        {/* Custom-charge mode (admin-generated payment link) */}
        {customCharge && (
          <>
            <div style={{ background: 'rgba(0,255,148,0.06)', border: '1px solid rgba(0,255,148,0.3)', padding: 16, marginBottom: 16 }} data-testid="custom-charge-banner">
              <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-success)', fontWeight: 700, letterSpacing: '0.15em' }}>CUSTOM PAYMENT LINK</p>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--pp-text-2)', lineHeight: 1.5 }}>
                Admin has set a custom amount for your <strong>Instant ${customCharge.account_size?.toLocaleString()}</strong> funded account. Pick a crypto to pay.
              </p>
              {customCharge.admin_message && (
                <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--pp-text-3)', fontStyle: 'italic' }}>Note: "{customCharge.admin_message}"</p>
              )}
            </div>
            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, marginBottom: 20, textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.2em', fontWeight: 700 }}>AMOUNT DUE</p>
              <p style={{ margin: '8px 0 0', fontSize: 36, fontWeight: 900, fontFamily: 'monospace', color: 'var(--pp-success)' }}>${customCharge.amount_usd?.toLocaleString()}</p>
            </div>
            <p style={LABEL}>PAY WITH</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 20 }}>
              {currencies.map(c => {
                const active = selectedCurrency === c.ticker;
                return (
                  <button key={c.ticker} data-testid={`currency-${c.ticker}`} onClick={() => setSelectedCurrency(c.ticker)} style={{ background: active ? 'rgba(41,121,255,0.12)' : 'var(--pp-surface)', border: `1px solid ${active ? 'var(--pp-accent)' : 'var(--pp-border)'}`, padding: 12, cursor: 'pointer', color: 'var(--pp-text)', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textAlign: 'left' }}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <button data-testid="pay-charge-btn" onClick={createInvoiceFromCharge} disabled={creating} className="btn-primary" style={{ width: '100%', height: 56, fontSize: 14, letterSpacing: '0.12em' }}>
              {creating ? 'GENERATING INVOICE...' : `PAY $${customCharge.amount_usd} WITH ${(selectedCurrency || '').toUpperCase()}`}
            </button>
          </>
        )}

        {/* Funded banner (only when not in custom-charge mode) */}
        {!customCharge && checkoutType === 'funded' && (
          <div style={{ background: 'rgba(0,255,148,0.06)', border: '1px solid rgba(0,255,148,0.25)', padding: 14, marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-success)', fontWeight: 700, letterSpacing: '0.1em' }}>PROFITPULSE FUNDED — ONE-TIME FEE</p>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
              Your application was submitted. Complete this one-time payment to activate your Instant funded account. Once confirmed, we'll provision your MT5 credentials within 24 hours.
            </p>
          </div>
        )}

        {/* Already paid? Enter AS passcode — subscription: instant activation; funded: admin-review request */}
        {!customCharge && checkoutType === 'subscription' && (
        <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.15em', color: 'var(--pp-text-3)', fontWeight: 700, marginBottom: 8 }}>ALREADY PAID?</p>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--pp-text-2)', lineHeight: 1.5 }}>Enter your Active Student (AS) passcode to skip payment and activate immediately.</p>
          {!meKycVerified && (
            <div data-testid="kyc-required-banner" style={{ background: 'rgba(255,176,0,0.1)', border: '1px solid rgba(255,176,0,0.4)', padding: 10, marginBottom: 10, fontSize: 11, color: '#FFB000', lineHeight: 1.5 }}>
              ⚠️ Identity verification required before redeeming. <a href="/verification" style={{ color: '#FFB000', fontWeight: 700, textDecoration: 'underline' }}>Verify now</a> (~2 min)
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <input data-testid="as-passcode-input" style={{ ...INPUT, flex: 1, fontFamily: 'monospace' }} placeholder="AS-XXXX-XXXX" value={asPasscode} onChange={e => setAsPasscode(e.target.value.toUpperCase())} />
            <button data-testid="redeem-as-btn" onClick={redeemASPasscode} disabled={!asPasscode.trim()} style={{ padding: '0 20px', background: 'var(--pp-success)', color: '#000', border: 'none', fontWeight: 700, fontSize: 12, letterSpacing: '0.1em', cursor: 'pointer' }}>REDEEM</button>
          </div>
        </div>
        )}

        {/* Funded AS request — admin-reviewed */}
        {!customCharge && checkoutType === 'funded' && (
        <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 16 }} data-testid="funded-as-section">
          <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.15em', color: 'var(--pp-text-3)', fontWeight: 700, marginBottom: 8 }}>ALREADY PAID FOR FUNDED?</p>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--pp-text-2)', lineHeight: 1.5 }}>
            If you've pre-paid (wire transfer, previous crypto payment, etc.), enter your AS passcode below along with a note. Admin will verify and either activate your account (if payment confirmed) or email you a custom payment link for the remaining amount.
          </p>
          <input data-testid="funded-as-passcode-input" style={{ ...INPUT, fontFamily: 'monospace', marginBottom: 10 }} placeholder="AS-XXXX-XXXX" value={asPasscode} onChange={e => setAsPasscode(e.target.value.toUpperCase())} />
          <textarea data-testid="funded-as-notes" rows={3} style={{ ...INPUT, resize: 'vertical', marginBottom: 10 }} placeholder="Optional — payment reference, tx hash, date paid, or anything to help admin verify..." value={asNotes} onChange={e => setAsNotes(e.target.value)} />
          <button data-testid="submit-funded-as-request-btn" onClick={submitFundedASRequest} disabled={!asPasscode.trim() || asSubmitting || !myFundedApplication} style={{ width: '100%', height: 42, background: 'var(--pp-accent)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, letterSpacing: '0.1em', cursor: (asPasscode.trim() && myFundedApplication && !asSubmitting) ? 'pointer' : 'not-allowed', opacity: (asPasscode.trim() && myFundedApplication && !asSubmitting) ? 1 : 0.6 }}>
            {asSubmitting ? 'SUBMITTING...' : myFundedApplication ? 'SUBMIT FOR ADMIN REVIEW' : 'NO FUNDED APPLICATION FOUND'}
          </button>
          {!myFundedApplication && (
            <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
              You need to apply for a funded account first. Go to <a href="/link-broker" style={{ color: 'var(--pp-accent)' }}>Link Broker → Apply for Funded</a>.
            </p>
          )}
        </div>
        )}

        {/* Plan picker — hide in custom-charge mode */}
        {!customCharge && (<>
        <p style={LABEL}>{checkoutType === 'funded' ? 'CHOOSE ACCOUNT SIZE' : 'CHOOSE PLAN'}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
          {plans.map(p => {
            const active = selectedPlan === p.id;
            return (
              <button key={p.id} data-testid={`plan-${p.id}`} onClick={() => setSelectedPlan(p.id)} style={{ background: active ? 'rgba(0,255,148,0.08)' : 'var(--pp-surface)', border: `1px solid ${active ? 'var(--pp-success)' : 'var(--pp-border)'}`, padding: 16, textAlign: 'left', cursor: 'pointer', color: 'var(--pp-text)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{p.label}</p>
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Per {p.period}{p.bundle ? ` · ${p.strategies}+ Strategies` : ''}</p>
                  </div>
                  <p style={{ margin: 0, fontSize: 22, fontWeight: 900, fontFamily: 'monospace', color: active ? 'var(--pp-success)' : 'var(--pp-text)' }}>${p.price_usd}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Currency picker */}
        <p style={LABEL}>PAY WITH</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 20 }}>
          {currencies.map(c => {
            const active = selectedCurrency === c.ticker;
            return (
              <button key={c.ticker} data-testid={`currency-${c.ticker}`} onClick={() => setSelectedCurrency(c.ticker)} style={{ background: active ? 'rgba(41,121,255,0.12)' : 'var(--pp-surface)', border: `1px solid ${active ? 'var(--pp-accent)' : 'var(--pp-border)'}`, padding: 12, cursor: 'pointer', color: 'var(--pp-text)', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textAlign: 'left' }}>
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Discount code */}
        <p style={LABEL}>DISCOUNT CODE (OPTIONAL)</p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <input data-testid="discount-code-input" style={{ ...INPUT, flex: 1, fontFamily: 'monospace' }} placeholder="PPP-XXXXXXXX" value={discountCode} onChange={e => setDiscountCode(e.target.value.toUpperCase())} />
          <button data-testid="apply-discount-btn" onClick={applyDiscount} disabled={!discountCode.trim()} style={{ padding: '0 18px', background: 'var(--pp-surface)', color: 'var(--pp-text)', border: '1px solid var(--pp-border)', fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer' }}>APPLY</button>
        </div>
        {discountApplied && (
          <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--pp-success)' }}>✓ {discountApplied.percent}% OFF applied ({discountApplied.code})</p>
        )}
        <button data-testid="request-discount-btn" onClick={() => setShowDiscountRequest(true)} style={{ background: 'none', border: 'none', color: 'var(--pp-accent)', padding: 0, cursor: 'pointer', fontSize: 12, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Gift size={13} /> Request a custom discount code
        </button>

        {/* Summary */}
        <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
            <span style={{ color: 'var(--pp-text-3)' }}>Subtotal</span>
            <span style={{ fontFamily: 'monospace' }}>${basePrice}</span>
          </div>
          {discountApplied && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, color: 'var(--pp-success)' }}>
              <span>Discount ({discountApplied.percent}%)</span>
              <span style={{ fontFamily: 'monospace' }}>-${(basePrice - discountedPrice).toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, paddingTop: 8, borderTop: '1px solid var(--pp-border)' }}>
            <span>Total</span>
            <span style={{ fontFamily: 'monospace' }}>${discountedPrice}</span>
          </div>
        </div>

        <button data-testid="pay-now-btn" onClick={createInvoice} disabled={creating || (selectedPlanObj?.account_type === 'custom')} className="btn-primary" style={{ width: '100%', height: 56, fontSize: 14, letterSpacing: '0.12em', opacity: (selectedPlanObj?.account_type === 'custom') ? 0.4 : 1 }}>
          {creating ? 'GENERATING INVOICE...' : (selectedPlanObj?.account_type === 'custom'
            ? 'CUSTOM — CONTACT SALES (NOT AVAILABLE VIA CHECKOUT)'
            : checkoutType === 'funded'
              ? `PAY $${discountedPrice} WITH ${(selectedCurrency || '').toUpperCase()} · FUND ACCOUNT`
              : `PAY $${discountedPrice} WITH ${(selectedCurrency || '').toUpperCase()}`)}
        </button>

        {/* Revolut manual pay option */}
        {!customCharge && selectedPlanObj?.account_type !== 'custom' && (
          <div data-testid="revolut-manual-pay" style={{ marginTop: 12, background: 'var(--pp-surface)', border: '1px solid rgba(0,255,148,0.3)', padding: 16 }}>
            <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.15em', color: 'var(--pp-success)', fontWeight: 700, marginBottom: 10 }}>OR PAY WITH REVOLUT</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--pp-text-2)' }}>Send to tag</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="mono" style={{ fontSize: 14, color: 'var(--pp-success)', fontWeight: 700 }}>@treptradingtech</span>
                <button data-testid="copy-revolut-tag" onClick={() => { navigator.clipboard.writeText('@treptradingtech'); toast.success('Revolut tag copied'); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', padding: '3px 8px', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Copy size={11} />COPY
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: 'var(--pp-text-2)' }}>Amount</span>
              <span className="mono" style={{ fontSize: 15, color: 'var(--pp-text)', fontWeight: 700 }}>${discountedPrice} USD</span>
            </div>
            <p style={{ margin: '8px 0 10px', fontSize: 11, color: 'var(--pp-text-3)', lineHeight: 1.6 }}>
              After sending, submit your Revolut reference below and admin will manually verify + activate your account.
            </p>
            <input data-testid="revolut-reference" style={{ ...INPUT, fontFamily: 'monospace', marginBottom: 8 }} placeholder="Revolut transaction reference or note" value={asNotes} onChange={e => setAsNotes(e.target.value)} />
            <button
              data-testid="submit-revolut-pay-btn"
              onClick={async () => {
                if (!asNotes.trim()) { toast.error('Please paste your Revolut reference'); return; }
                try {
                  await api.submitManualPayment?.({
                    plan_id: selectedPlan,
                    method: 'revolut',
                    reference: asNotes.trim(),
                    amount_usd: discountedPrice,
                  });
                  toast.success("Submitted! We'll verify & activate within 24h.");
                  setAsNotes('');
                } catch (e) { toast.error(e.message || 'Failed to submit'); }
              }}
              style={{ width: '100%', height: 40, background: 'var(--pp-success)', color: '#000', border: 'none', fontSize: 11, fontWeight: 800, letterSpacing: '0.15em', cursor: 'pointer' }}
            >I'VE PAID — SUBMIT FOR VERIFICATION</button>
            <p style={{ margin: '10px 0 0', fontSize: 10, color: 'var(--pp-text-3)', lineHeight: 1.6, textAlign: 'center' }}>
              Don't have Revolut?&nbsp;
              <a href="https://revolut.com/referral/?referral-code=2ndadam!MAY1-26-AR-US-H2-REFBLOCK&geo-redirect" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--pp-accent)', textDecoration: 'underline', fontWeight: 700 }}>Sign up free with our referral link</a>
            </p>
          </div>
        )}
        </>)}
      </div>

      {showDiscountRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowDiscountRequest(false)}>
          <div data-testid="discount-request-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, width: 480, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, marginBottom: 6 }}>REQUEST A DISCOUNT CODE</p>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>Tell the admin why you need a discount. Once approved, you'll receive an email with a unique code you can use on this page.</p>

            <div style={{ marginBottom: 12 }}>
              <label style={LABEL}>Requested %</label>
              <input data-testid="discount-request-percent" type="number" min={1} max={100} value={discountRequestedPct} onChange={e => setDiscountRequestedPct(e.target.value)} style={INPUT} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={LABEL}>Reason / Notes</label>
              <textarea data-testid="discount-request-reason" rows={4} value={discountReason} onChange={e => setDiscountReason(e.target.value)} placeholder="Student community member, funded account trader, etc." style={{ ...INPUT, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowDiscountRequest(false)} style={{ flex: 1, height: 42, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em' }}>CANCEL</button>
              <button data-testid="submit-discount-request-btn" onClick={submitDiscountRequest} disabled={submittingDiscountRequest || !discountReason.trim()} style={{ flex: 1, height: 42, background: 'var(--pp-accent)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em' }}>
                {submittingDiscountRequest ? 'SENDING...' : 'SUBMIT'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
